import React, { useRef } from 'react';
import { FormLabel, FormError, ProfilePicture, Loader } from '@getro/rombo';
import { Input } from '@rebass/forms/styled-components';
import { Plus } from 'lucide-react';
import PropTypes from 'prop-types';
import { Box, Flex, Text } from 'rebass/styled-components';
import { useUploadFile } from 'hooks/useUploadFile';

export const FormImage = ({
  label,
  labelExtra,
  helpText,
  field,
  form,
  width,
  height,
  imagePlaceholder,
  containerProps,
  ...rest
}) => {
  const fileInputRef = useRef(null);
  const { id, name, value } = field;
  const { setFieldValue } = form;
  const error = form.touched?.[name] && form.errors?.[name];
  const { uploadFile, isLoading } = useUploadFile();

  const onChangeFile = (e) => {
    const file = e.target.files[0];

    if (file) {
      uploadFile(file).then((remoteUrl) => {
        form.setFieldValue(name, remoteUrl);
      });
    }
  };

  const onRemove = (e) => {
    e.stopPropagation();

    setFieldValue(name, null);
  };

  return (
    <Box width="100%" {...containerProps}>
      {label && (
        <FormLabel htmlFor={name || id} extra={labelExtra}>
          {label}
        </FormLabel>
      )}
      {helpText && (
        <Text fontSize="1" mb="2" lineHeight="20px">
          {helpText}
        </Text>
      )}

      <Flex alignItems="center" onClick={() => fileInputRef.current.click()} sx={{ cursor: 'pointer' }}>
        {isLoading ? (
          <Flex width="64px" height="64px" alignItems="center" justifyContent="center">
            <Loader size="small" />
          </Flex>
        ) : (
          <ProfilePicture name={imagePlaceholder} width={width} height={height} imageUrl={value} {...rest} />
        )}
        <Flex alignItems="center" ml="3">
          {!value && (
            <>
              <Box as={Plus} width="16px" height="16px" aria-hidden="true" mr="2" />
              <Text fontSize="1" fontWeight="medium">
                Add image
              </Text>
            </>
          )}
          {value && (
            <Flex
              sx={{
                columnGap: '16px',
              }}
            >
              <Text onClick={onRemove} fontSize="1" fontWeight="medium">
                Remove
              </Text>
              <Text fontSize="1" fontWeight="medium">
                Change
              </Text>
            </Flex>
          )}
        </Flex>
      </Flex>

      {error && <FormError>{error}</FormError>}

      <Input ref={fileInputRef} type="file" accept="image/*" onChange={onChangeFile} sx={{ display: 'none' }} />
    </Box>
  );
};

FormImage.propTypes = {
  label: PropTypes.oneOfType([PropTypes.node, PropTypes.string]),
  labelExtra: PropTypes.string,
  helpText: PropTypes.oneOfType([PropTypes.node, PropTypes.string]),
  width: PropTypes.string,
  height: PropTypes.string,
  imagePlaceholder: PropTypes.string,
  containerProps: PropTypes.object,
  field: PropTypes.shape({
    id: PropTypes.string,
    name: PropTypes.string,
    value: PropTypes.string,
    onChange: PropTypes.func,
  }),
  form: PropTypes.shape({
    setFieldValue: PropTypes.func,
    errors: PropTypes.oneOfType([PropTypes.object, PropTypes.array]),
    touched: PropTypes.oneOfType([PropTypes.object, PropTypes.array]),
  }),
};

FormImage.defaultProps = {
  label: null,
  labelExtra: '',
  helpText: null,
  width: '64px',
  height: '64px',
  imagePlaceholder: 'Getro',
  containerProps: {},
  field: {},
  form: { errors: [] },
};
